/**
 * The clock of the world: one day every hour of real time, half of it light and half dark, and the
 * named times of day (ช่วงเวลา) the screen shows. Pure data and pure functions: the hour only moves in
 * `World.step` (no Date, no timers).
 */

/** One in-game day every 60 real minutes (150 real seconds an in-game hour). */
export const DAY_SECONDS = 60 * 60;

/** Daylight runs from sunrise to sunset: 12 in-game hours, so 30 real minutes light and 30 dark. */
export const DAYLIGHT = { sunrise: 6, sunset: 18 } as const;

/** Whether the sun is up at an hour (06:00 to 18:00). */
export const isDaytime = (hour: number): boolean => hour >= DAYLIGHT.sunrise && hour < DAYLIGHT.sunset;

export interface TimePeriod {
  readonly id: string;
  /** The Thai name shown on the clock. */
  readonly name: string;
  /** From this hour (inclusive) to `to` (exclusive); `to` < `from` wraps past midnight. */
  readonly from: number;
  readonly to: number;
}

/** The named times of day, in order from midnight, covering all 24 hours without a gap. */
export const TIME_PERIODS: readonly TimePeriod[] = [
  { id: 'midnight', name: 'เที่ยงคืน', from: 23, to: 1 },
  { id: 'deep', name: 'ดึกสงัด', from: 1, to: 4 },
  { id: 'predawn', name: 'ใกล้รุ่ง', from: 4, to: 6 },
  { id: 'daybreak', name: 'รุ่งเช้า', from: 6, to: 7 },
  { id: 'early', name: 'เช้าตรู่', from: 7, to: 9 },
  { id: 'late', name: 'ช่วงสาย', from: 9, to: 11 },
  { id: 'noon', name: 'เที่ยงวัน', from: 11, to: 13 },
  { id: 'afternoon', name: 'บ่าย', from: 13, to: 16 },
  { id: 'evening', name: 'เย็น', from: 16, to: 18 },
  { id: 'dusk', name: 'พลบค่ำ', from: 18, to: 19 },
  { id: 'nightfall', name: 'หัวค่ำ', from: 19, to: 21 },
  { id: 'night', name: 'ดึก', from: 21, to: 23 },
];

const inPeriod = (p: TimePeriod, h: number): boolean =>
  p.from < p.to ? h >= p.from && h < p.to : h >= p.from || h < p.to;

/** The named time of day at an hour (0–24, wrapped). */
export function periodAt(hour: number): TimePeriod {
  const h = ((hour % 24) + 24) % 24;
  return TIME_PERIODS.find((p) => inPeriod(p, h)) ?? TIME_PERIODS[0]!;
}
